import React, { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { Icon } from "Components";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { DEFAULT_CAMPAIGNS_PAGESIZE } from "PhishingManager/PhishingManager.constants";
import PropertySelect from "../CampaignSteps/components/PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import SelectedLabel from "./SelectedLabel";

interface IndividualOption {
    key: string;
    label: string;
    email: string;
    propertyKey: string;
}

interface PropertyOption {
    value: string;
    label: string;
    count?: number;
}

interface Props {
    propertyOptions: PropertyOption[];
    individualOptions: IndividualOption[];
    selectedIndividuals: string[];
    setSelectedIndividuals: (individuals: string[]) => void;
    individualSearchQuery: string;
    setIndividualSearchQuery: (value: string) => void;
}

const IndividualRecipients: React.FC<Props> = ({
    propertyOptions,
    individualOptions,
    selectedIndividuals,
    setSelectedIndividuals,
    individualSearchQuery,
    setIndividualSearchQuery,
}) => {
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [allIndividualsSelected, setAllIndividualsSelected] = useState(false);
    const [individualsPage, setIndividualsPage] = useState(1);
    const [visibleIndividuals, setVisibleIndividuals] = useState<IndividualOption[]>([]);
    const [hasMoreIndividuals, setHasMoreIndividuals] = useState(false);
    const [individualsLoading, setIndividualsLoading] = useState(false);

    const filteredIndividuals = useMemo(() => {
        return individualOptions.filter(
            (ind) =>
                (allSelected || selectedProperties.includes(ind.propertyKey)) &&
                (
                    ind.label.toLowerCase().includes(individualSearchQuery.toLowerCase()) ||
                    ind.email.toLowerCase().includes(individualSearchQuery.toLowerCase())
                )
        );
    }, [individualOptions, selectedProperties, allSelected, individualSearchQuery]);

    const totalUsersCount = filteredIndividuals
        .filter((r) => selectedIndividuals.includes(r.key)).length;

    const handleSelectAllIndividuals = () => {
        setAllIndividualsSelected(!allIndividualsSelected);
    };

    const handleIndividualToggle = (key: string) => {
        if (selectedIndividuals.includes(key)) {
            setSelectedIndividuals(selectedIndividuals.filter((i) => i !== key));
        } else {
            setSelectedIndividuals([...selectedIndividuals, key]);
        }
    };

    const loadMoreIndividuals = () => {
        setIndividualsLoading(true);
        setTimeout(() => {
            const nextPage = individualsPage + 1;
            const nextItems = filteredIndividuals.slice(0, nextPage * DEFAULT_CAMPAIGNS_PAGESIZE);
            setVisibleIndividuals(nextItems);
            setIndividualsPage(nextPage);
            setHasMoreIndividuals(nextItems.length < filteredIndividuals.length);
            setIndividualsLoading(false);
        }, 1000);
    };

    useEffect(() => {
        const firstPage = filteredIndividuals.slice(0, DEFAULT_CAMPAIGNS_PAGESIZE);
        setVisibleIndividuals(firstPage);
        setIndividualsPage(1);
        setHasMoreIndividuals(filteredIndividuals.length > DEFAULT_CAMPAIGNS_PAGESIZE);
    }, [filteredIndividuals]);

    useEffect((() => {
        if (!!allIndividualsSelected) {
            setSelectedIndividuals(filteredIndividuals.map((ind) => ind.key));
        } else {
            setSelectedIndividuals([]);
        }
    }), [allIndividualsSelected]);

    useEffect((() => {
        if (!allSelected) {
            setSelectedIndividuals([]);
        }
    }), [allSelected]);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect
                        propertyOptions={propertyOptions}
                        selectedProperties={selectedProperties}
                        setSelectedProperties={setSelectedProperties}
                        allSelected={allSelected}
                        setAllSelected={setAllSelected}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedIndividuals(
                                selectedIndividuals.filter((indId) => {
                                    const ind = individualOptions.find((i) => i.key === indId);
                                    return ind?.propertyKey !== propertyValue;
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <Input.Search
                            placeholder="Search by name or email"
                            value={individualSearchQuery}
                            onChange={(e: any) => setIndividualSearchQuery(e.target.value)}
                            suffix={<Icon type="search" />}
                        />
                    </div>
                </div>
            </div>

            <SelectedLabel labelText={`Select individuals ${filteredIndividuals.length > 0 ? `(${filteredIndividuals.length} Results)` : ''}`} totalCount={totalUsersCount} selectedAll={((filteredIndividuals.length > 0 && filteredIndividuals.every((ind) => selectedIndividuals.includes(ind.key))))} handleSelectAll={handleSelectAllIndividuals} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see individuals.
                    </div>
                ) : filteredIndividuals.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No individuals found.
                    </div>
                ) : (
                    <InfinityScrollWrapper
                        dataLength={visibleIndividuals.length}
                        hasMore={hasMoreIndividuals}
                        endMessageText=""
                        onLoadMore={loadMoreIndividuals}
                        loading={individualsLoading}
                        height={400}
                        scrollableId="individual-list-infinite-scroll"
                        loaderText="Loading individuals..."
                    >
                        {visibleIndividuals.map((individual) => (
                            <RecipientItem
                                key={individual.key}
                                title={individual.label}
                                description={individual.email}
                                iconType="recipientIndividual"
                                isChecked={selectedIndividuals.includes(individual.key)}
                                onToggle={() => handleIndividualToggle(individual.key)}
                            />
                        ))}
                    </InfinityScrollWrapper>
                )}
            </div>
        </div>
    );
};

export default IndividualRecipients;